import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Row, Col, Card, Button, Badge, Form, Alert, Spinner, ListGroup, Modal, Tab, Tabs, ProgressBar } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { useAuth } from '../../hooks/useAuth'
import { api } from '../../services/api'
import { TicketRating } from '../../components/TicketRating'

interface Ticket {
  id: string
  subject: string
  description: string
  status: string
  priority: string
  category?: string
  createdAt: string
  updatedAt: string
  org?: {
    id: string
    name: string
    slug: string
  }
  requester: {
    id: string
    name: string
    email: string
  }
  assignee?: {
    id: string
    name: string
    email: string
  }
  tags: Array<{
    tag: {
      name: string
    }
  }>
  comments: Array<{
    id: string
    body: string
    createdAt: string
    author: {
      name: string
      email: string
    }
    visibility: string
    attachments: Array<{
      id: string
      fileName: string
      size: number
      contentType: string
    }>
  }>
  attachments: Array<{
    id: string
    fileName: string
    size: number
    contentType: string
    uploadedBy?: {
      name: string
    }
  }>
  statusHistory: Array<{
    id: string
    fromStatus: string
    toStatus: string
    changedAt: string
    changedBy: {
      name: string
    }
  }>
  rating?: number
  ratingComment?: string
  ratedAt?: string
}

interface SLAData {
  responseTime: number
  resolutionTime: number
  isOverdue: boolean
  timeRemaining: number
}

export const EnhancedTicketDetailsPage = () => {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth() // eslint-disable-line @typescript-eslint/no-unused-vars
  const navigate = useNavigate()
  const [ticket, setTicket] = useState<Ticket | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState('overview')
  const [newComment, setNewComment] = useState('')
  const [isInternalComment, setIsInternalComment] = useState(false)
  const [submittingComment, setSubmittingComment] = useState(false)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [showAssigneeModal, setShowAssigneeModal] = useState(false)
  const [showPriorityModal, setShowPriorityModal] = useState(false)
  const [showTagsModal, setShowTagsModal] = useState(false)
  const [newStatus, setNewStatus] = useState('')
  const [newAssignee, setNewAssignee] = useState('')
  const [newPriority, setNewPriority] = useState('')
  const [newTags, setNewTags] = useState<string[]>([])
  const [availableUsers, setAvailableUsers] = useState<any[]>([])
  const [slaData, setSlaData] = useState<SLAData | null>(null)
  const [attachments, setAttachments] = useState<File[]>([])

  useEffect(() => {
    if (id) {
      fetchTicket()
      fetchUsers()
      fetchSLAData()
    }
  }, [id])

  const fetchTicket = async () => {
    try {
      setLoading(true)
      setError('')
      const response = await api.get(`/tickets/${id}`)
      setTicket(response.data.data)
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to load ticket'
      if (err?.response?.status === 404) {
        setError('Ticket not found. You may not have permission to view this ticket.')
      } else if (err?.response?.status === 403) {
        setError('Access denied. You do not have permission to view this ticket.')
      } else {
        setError(`Failed to load ticket: ${errorMessage}`)
      }
      console.error('Error fetching ticket:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users')
      setAvailableUsers(response.data.data || [])
    } catch (err) {
    }
  }

  // Removed unused fetchTags and availableTags state

  const fetchSLAData = async () => {
    try {
      const response = await api.get(`/tickets/${id}/sla`)
      setSlaData(response.data.data)
    } catch (err) {
    }
  }

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newComment.trim()) return

    setSubmittingComment(true)
    try {
      const formData = new FormData()
      formData.append('body', newComment) // Changed from 'content' to 'body'
      formData.append('visibility', isInternalComment ? 'INTERNAL' : 'PUBLIC') // Changed from 'isInternal' to 'visibility'
      
      attachments.forEach((file) => {
        formData.append(`attachments`, file)
      })

      await api.post(`/tickets/${id}/comments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      setNewComment('')
      setAttachments([])
      setIsInternalComment(false)
      fetchTicket()
    } catch (err: any) {
      console.error('Failed to add comment:', err)
    } finally {
      setSubmittingComment(false)
    }
  }

  const handleStatusChange = async () => {
    try {
      await api.patch(`/tickets/${id}`, { status: newStatus })
      
      // Show specific success message for closing ticket
      if (newStatus === 'CLOSED') {
        toast.success('Ticket closed successfully!', {
          icon: '✅',
          autoClose: 3000
        })
      } else {
        toast.success('Ticket status updated successfully')
      }
      
      setShowStatusModal(false)
      setNewStatus('')
      fetchTicket()
      
      // Dispatch event to update ticket count in sidebar
      window.dispatchEvent(new CustomEvent('ticket-status-changed'))
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Failed to update ticket status'
      toast.error(errorMessage)
    }
  }

  const handleAssigneeChange = async () => {
    try {
      await api.patch(`/tickets/${id}`, { assigneeId: newAssignee || null })
      setShowAssigneeModal(false)
      fetchTicket()
    } catch (err: any) {
    }
  }

  const handlePriorityChange = async () => {
    try {
      await api.patch(`/tickets/${id}`, { priority: newPriority })
      setShowPriorityModal(false)
      fetchTicket()
    } catch (err: any) {
    }
  }

  const handleTagsChange = async () => {
    try {
      await api.patch(`/tickets/${id}`, { tags: newTags })
      setShowTagsModal(false)
      fetchTicket()
    } catch (err: any) {
    }
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    setAttachments(prev => [...(prev || []), ...files])
  }

  const removeAttachment = (index: number) => {
    setAttachments(prev => (prev || []).filter((_, i) => i !== index))
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Urgent': return 'danger'
      case 'High': return 'warning'
      case 'Medium': return 'info'
      case 'Low': return 'secondary'
      default: return 'secondary'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'OPEN': return 'primary'
      case 'IN_PROGRESS': return 'warning'
      case 'RESOLVED': return 'success'
      case 'CLOSED': return 'secondary'
      default: return 'secondary'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    )
  }

  if (error || !ticket) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Error</Alert.Heading>
        <p>{error || 'Ticket not found'}</p>
        <Button onClick={() => navigate('/tickets')} variant="outline-danger">
          Back to Tickets
        </Button>
      </Alert>
    )
  }

  return (
    <div className="enhanced-ticket-details-container">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="mb-1">{ticket.subject}</h1>
          <p className="text-muted mb-0">Ticket #{ticket.id}</p>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={() => navigate('/tickets')}>
            <i className="fas fa-arrow-left me-2"></i>Back to Tickets
          </Button>
          <Button variant="outline-primary" onClick={() => setShowStatusModal(true)}>
            <i className="fas fa-edit me-2"></i>Change Status
          </Button>
        </div>
      </div>

      {/* Resolution Confirmation Banner for Requesters */}
      {user?.role === 'REQUESTER' && ticket.status === 'RESOLVED' && (
        <Alert variant="success" className="mb-4 d-flex align-items-center justify-content-between">
          <div>
            <h5 className="alert-heading mb-2">
              <i className="fas fa-check-circle me-2"></i>
              This ticket has been resolved!
            </h5>
            <p className="mb-0">
              If you're satisfied with the resolution, please confirm by closing this ticket. 
              If the issue persists, you can reopen it.
            </p>
          </div>
          <Button 
            variant="success"
            onClick={async () => {
              try {
                await api.patch(`/tickets/${id}`, { status: 'CLOSED' })
                toast.success('Ticket closed successfully!', {
                  icon: '✅',
                  autoClose: 3000
                })
                fetchTicket()
                window.dispatchEvent(new CustomEvent('ticket-status-changed'))
              } catch (err: any) {
                const errorMessage = err.response?.data?.error?.message || 'Failed to close ticket'
                toast.error(errorMessage)
              }
            }}
          >
            <i className="fas fa-check-double me-2"></i>
            Close Ticket
          </Button>
        </Alert>
      )}

      <Row>
        <Col lg={8}>
          {/* Main Content Tabs */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Body className="p-0">
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k || 'overview')}
                className="border-bottom"
              >
                <Tab eventKey="overview" title="Overview">
                  <div className="p-4">
                    <div className="mb-4">
                      <h5>Description</h5>
                      <p className="text-muted">{ticket.description}</p>
                    </div>

                    {ticket.tags.length > 0 && (
                      <div className="mb-4">
                        <h5>Tags</h5>
                        <div className="d-flex flex-wrap gap-1">
                          {ticket.tags.map((tag, index) => (
                            <Badge key={index} bg="secondary">
                              {tag.tag.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {ticket.attachments && ticket.attachments.length > 0 && (
                      <div className="mb-4">
                        <Alert variant="info" className="d-flex justify-content-between align-items-center">
                          <div>
                            <i className="fas fa-paperclip me-2"></i>
                            <strong>{ticket.attachments.length}</strong> attachment{ticket.attachments.length !== 1 ? 's' : ''} available
                          </div>
                          <Button 
                            variant="info" 
                            size="sm"
                            onClick={() => setActiveTab('attachments')}
                          >
                            View Attachments <i className="fas fa-arrow-right ms-1"></i>
                          </Button>
                        </Alert>
                      </div>
                    )}
                  </div>
                </Tab>

                <Tab eventKey="comments" title="Comments">
                  <div className="p-4">
                    {ticket.comments.length > 0 ? (
                      <div className="comments-list">
                        {ticket.comments.map((comment) => (
                          <div key={comment.id} className={`comment-item border-bottom pb-3 mb-3 ${comment.visibility === 'INTERNAL' ? 'bg-light p-3 rounded' : ''}`}>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div>
                                <strong>{comment.author.name}</strong>
                                <small className="text-muted ms-2">{formatDate(comment.createdAt)}</small>
                                {comment.visibility === 'INTERNAL' && (
                                  <Badge bg="warning" className="ms-2">Internal</Badge>
                                )}
                              </div>
                            </div>
                            <p className="mb-2">{comment.body}</p>
                            
                            {comment.attachments && comment.attachments.length > 0 && (
                              <div className="mt-2">
                                <small className="text-muted">Attachments:</small>
                                <div className="d-flex flex-wrap gap-1 mt-1">
                                  {comment.attachments.map((attachment) => (
                                    <Badge 
                                      key={attachment.id} 
                                      bg="light" 
                                      text="dark" 
                                      className="d-flex align-items-center gap-1"
                                      style={{ cursor: 'pointer' }}
                                      onClick={async () => {
                                        try {
                                          const response = await api.get(`/tickets/${ticket.id}/attachments/${attachment.id}`, {
                                            responseType: 'blob'
                                          })
                                          const url = window.URL.createObjectURL(new Blob([response.data]))
                                          const link = document.createElement('a')
                                          link.href = url
                                          link.setAttribute('download', attachment.fileName)
                                          document.body.appendChild(link)
                                          link.click()
                                          link.remove()
                                          window.URL.revokeObjectURL(url)
                                        } catch (error) {
                                          console.error('Failed to download attachment:', error)
                                          toast.error('Failed to download attachment')
                                        }
                                      }}
                                    >
                                      <i className="fas fa-paperclip"></i>
                                      {attachment.fileName}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <i className="fas fa-comments text-muted" style={{ fontSize: '2rem' }}></i>
                        <p className="text-muted mt-2">No comments yet</p>
                      </div>
                    )}

                    {/* Add Comment Form */}
                    <div className="border-top pt-3">
                      <Form onSubmit={handleAddComment}>
                        <Form.Group className="mb-3">
                          <Form.Label>Add a comment</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Write your comment here..."
                          />
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Check
                            type="checkbox"
                            label="Internal comment (not visible to requester)"
                            checked={isInternalComment}
                            onChange={(e) => setIsInternalComment(e.target.checked)}
                          />
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Attachments (optional)</Form.Label>
                          <Form.Control
                            type="file"
                            multiple
                            onChange={handleFileUpload}
                            accept="image/*,application/pdf,text/*,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          />
                        </Form.Group>

                        {attachments && attachments.length > 0 && (
                          <div className="mb-3">
                            <small className="text-muted">Selected files:</small>
                            <div className="d-flex flex-wrap gap-1 mt-1">
                              {attachments.map((file, index) => (
                                <Badge key={index} bg="primary" className="d-flex align-items-center gap-1">
                                  <i className="fas fa-paperclip"></i>
                                  {file.name}
                                  <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    style={{ fontSize: '0.7em' }}
                                    onClick={() => removeAttachment(index)}
                                  />
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="d-flex justify-content-end">
                          <Button 
                            type="submit" 
                            variant="primary"
                            disabled={submittingComment || !newComment.trim()}
                          >
                            {submittingComment ? (
                              <>
                                <Spinner size="sm" className="me-2" />
                                Adding...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-paper-plane me-2"></i>
                                Add Comment
                              </>
                            )}
                          </Button>
                        </div>
                      </Form>
                    </div>
                  </div>
                </Tab>

                <Tab eventKey="history" title="History">
                  <div className="p-4">
                    <h5>Status History</h5>
                    {ticket.statusHistory.length > 0 ? (
                      <div className="timeline">
                        {ticket.statusHistory.map((history) => (
                          <div key={history.id} className="timeline-item d-flex mb-3">
                            <div className="timeline-marker bg-primary rounded-circle me-3" style={{ width: '12px', height: '12px', marginTop: '6px' }}></div>
                            <div className="flex-grow-1">
                              <div className="fw-medium">
                                Status changed from <Badge bg="secondary">{history.fromStatus}</Badge> to <Badge bg="primary">{history.toStatus}</Badge>
                              </div>
                              <small className="text-muted">
                                by {history.changedBy.name} on {formatDate(history.changedAt)}
                              </small>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted">No status changes yet</p>
                    )}
                  </div>
                </Tab>

                <Tab eventKey="attachments" title={
                  <span>
                    Attachments {ticket.attachments.length > 0 && (
                      <Badge bg="info" pill className="ms-1">{ticket.attachments.length}</Badge>
                    )}
                  </span>
                }>
                  <div className="p-4">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h5 className="mb-0">
                        <i className="fas fa-paperclip me-2 text-primary"></i>
                        Ticket Attachments
                      </h5>
                      {ticket.attachments.length > 0 && (
                        <Badge bg="secondary">{ticket.attachments.length} file{ticket.attachments.length !== 1 ? 's' : ''}</Badge>
                      )}
                    </div>

                    {ticket.attachments.length > 0 ? (
                      <ListGroup variant="flush">
                        {ticket.attachments.map((attachment) => (
                          <ListGroup.Item key={attachment.id} className="border rounded mb-3 shadow-sm">
                            <div className="d-flex justify-content-between align-items-start">
                              <div className="d-flex align-items-start flex-grow-1">
                                <div className="me-3">
                                  <div 
                                    className="d-flex align-items-center justify-content-center rounded"
                                    style={{ 
                                      width: '48px', 
                                      height: '48px', 
                                      backgroundColor: '#f8f9fa',
                                      border: '2px solid #e9ecef'
                                    }}
                                  >
                                    <i className="fas fa-file-alt text-primary" style={{ fontSize: '24px' }}></i>
                                  </div>
                                </div>
                                <div className="flex-grow-1">
                                  <div className="fw-bold mb-1">
                                    <a 
                                      href="#" 
                                      onClick={async (e) => {
                                        e.preventDefault()
                                        try {
                                          const response = await api.get(`/tickets/${ticket.id}/attachments/${attachment.id}`, {
                                            responseType: 'blob'
                                          })
                                          const url = window.URL.createObjectURL(new Blob([response.data]))
                                          const link = document.createElement('a')
                                          link.href = url
                                          link.setAttribute('download', attachment.fileName)
                                          document.body.appendChild(link)
                                          link.click()
                                          link.remove()
                                          window.URL.revokeObjectURL(url)
                                        } catch (error) {
                                          console.error('Failed to download attachment:', error)
                                          toast.error('Failed to download attachment')
                                        }
                                      }}
                                      className="text-primary text-decoration-none"
                                      style={{ cursor: 'pointer' }}
                                    >
                                      {attachment.fileName}
                                    </a>
                                  </div>
                                  <div className="d-flex gap-3 text-muted small">
                                    <span>
                                      <i className="fas fa-hdd me-1"></i>
                                      {formatFileSize(attachment.size)}
                                    </span>
                                    <span>
                                      <i className="fas fa-tag me-1"></i>
                                      {attachment.contentType?.split('/')[1]?.toUpperCase() || 'FILE'}
                                    </span>
                                    {attachment.uploadedBy && (
                                      <span>
                                        <i className="fas fa-user me-1"></i>
                                        {attachment.uploadedBy.name}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="d-flex gap-2">
                                <Button 
                                  variant="outline-primary" 
                                  size="sm"
                                  title="Download attachment"
                                  onClick={async () => {
                                    try {
                                      const response = await api.get(`/tickets/${ticket.id}/attachments/${attachment.id}`, {
                                        responseType: 'blob'
                                      })
                                      const url = window.URL.createObjectURL(new Blob([response.data]))
                                      const link = document.createElement('a')
                                      link.href = url
                                      link.setAttribute('download', attachment.fileName)
                                      document.body.appendChild(link)
                                      link.click()
                                      link.remove()
                                      window.URL.revokeObjectURL(url)
                                    } catch (error) {
                                      console.error('Failed to download attachment:', error)
                                      toast.error('Failed to download attachment')
                                    }
                                  }}
                                >
                                  <i className="fas fa-download me-1"></i>
                                  Download
                                </Button>
                              </div>
                            </div>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    ) : (
                      <div className="text-center py-5">
                        <div className="mb-3">
                          <i className="fas fa-file-upload text-muted" style={{ fontSize: '4rem', opacity: 0.3 }}></i>
                        </div>
                        <h5 className="text-muted">No Attachments</h5>
                        <p className="text-muted mb-0">
                          This ticket doesn't have any attachments yet.
                        </p>
                      </div>
                    )}
                  </div>
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>

          {/* Rating Component for Resolved Tickets */}
          {ticket.status === 'RESOLVED' && user?.role === 'REQUESTER' && (
            <div className="mb-4">
              <TicketRating
                ticketId={ticket.id}
                currentRating={ticket.rating}
                currentComment={ticket.ratingComment}
                onRatingSubmitted={fetchTicket}
              />
            </div>
          )}
        </Col>

        <Col lg={4}>
          {/* Ticket Details Sidebar */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-white border-bottom">
              <h5 className="mb-0">
                <i className="fas fa-info me-2 text-info"></i>
                Ticket Details
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <strong>Status:</strong>
                <Badge bg={getStatusColor(ticket.status)} className="ms-2">
                  {ticket.status}
                </Badge>
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  className="ms-2"
                  onClick={() => setShowStatusModal(true)}
                >
                  <i className="fas fa-edit"></i>
                </Button>
              </div>
              
              <div className="mb-3">
                <strong>Priority:</strong>
                <Badge bg={getPriorityColor(ticket.priority)} className="ms-2">
                  {ticket.priority}
                </Badge>
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  className="ms-2"
                  onClick={() => setShowPriorityModal(true)}
                >
                  <i className="fas fa-edit"></i>
                </Button>
              </div>
              
              <div className="mb-3">
                <strong>Assignee:</strong>
                <div className="ms-2">
                  {ticket.assignee ? (
                    <div>
                      <div>{ticket.assignee.name}</div>
                      <small className="text-muted">{ticket.assignee.email}</small>
                    </div>
                  ) : (
                    <Badge bg="warning">Unassigned</Badge>
                  )}
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    className="ms-2"
                    onClick={() => setShowAssigneeModal(true)}
                  >
                    <i className="fas fa-edit"></i>
                  </Button>
                </div>
              </div>
              
              <div className="mb-3">
                <strong>Tags:</strong>
                <div className="ms-2">
                  {ticket.tags.length > 0 ? (
                    <div className="d-flex flex-wrap gap-1">
                      {ticket.tags.map((tag, index) => (
                        <Badge key={index} bg="secondary">
                          {tag.tag.name}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted">No tags</span>
                  )}
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    className="ms-2"
                    onClick={() => setShowTagsModal(true)}
                  >
                    <i className="fas fa-edit"></i>
                  </Button>
                </div>
              </div>
              
              {user?.role === 'ADMIN' && ticket.org && (
                <div className="mb-3">
                  <strong>Organization:</strong>
                  <div className="ms-2">
                    <Badge bg="info" className="text-dark">
                      {ticket.org.name}
                    </Badge>
                  </div>
                </div>
              )}
              
              <div className="mb-3">
                <strong>Requester:</strong>
                <div className="ms-2">
                  <div>{ticket.requester.name}</div>
                  <small className="text-muted">{ticket.requester.email}</small>
                </div>
              </div>
              
              <div className="mb-3">
                <strong>Created:</strong>
                <div className="ms-2">{formatDate(ticket.createdAt)}</div>
              </div>
              
              <div className="mb-0">
                <strong>Last Updated:</strong>
                <div className="ms-2">{formatDate(ticket.updatedAt)}</div>
              </div>

              {/* Rating Display */}
              {ticket.rating && (
                <div className="mt-3 pt-3 border-top">
                  <strong>Customer Rating:</strong>
                  <div className="ms-2">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i
                          key={star}
                          className={`fas fa-star ${star <= ticket.rating! ? 'text-warning' : 'text-muted'}`}
                        ></i>
                      ))}
                      <span className="fw-medium">{ticket.rating}/5</span>
                    </div>
                    {ticket.ratingComment && (
                      <div className="mt-2">
                        <small className="text-muted">Comment:</small>
                        <div className="bg-light p-2 rounded mt-1">
                          <small>{ticket.ratingComment}</small>
                        </div>
                      </div>
                    )}
                    <small className="text-muted d-block mt-1">
                      Rated on {formatDate(ticket.ratedAt!)}
                    </small>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* SLA Panel */}
          {slaData && (
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-white border-bottom">
                <h5 className="mb-0">
                  <i className="fas fa-clock me-2 text-warning"></i>
                  SLA Status
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span>Response Time</span>
                    <span className={slaData.isOverdue ? 'text-danger' : 'text-success'}>
                      {slaData.responseTime}h
                    </span>
                  </div>
                  <ProgressBar 
                    variant={slaData.isOverdue ? 'danger' : 'success'} 
                    now={Math.min((slaData.responseTime / 24) * 100, 100)} 
                  />
                </div>
                
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span>Resolution Time</span>
                    <span className={slaData.isOverdue ? 'text-danger' : 'text-success'}>
                      {slaData.resolutionTime}h
                    </span>
                  </div>
                  <ProgressBar 
                    variant={slaData.isOverdue ? 'danger' : 'success'} 
                    now={Math.min((slaData.resolutionTime / 72) * 100, 100)} 
                  />
                </div>
                
                <div className="text-center">
                  <Badge bg={slaData.isOverdue ? 'danger' : 'success'} className="fs-6">
                    {slaData.isOverdue ? 'Overdue' : 'On Track'}
                  </Badge>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* Quick Actions */}
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom">
              <h5 className="mb-0">
                <i className="fas fa-bolt me-2 text-warning"></i>
                Quick Actions
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-2">
                {/* Special "Confirm Resolution" button for requesters when ticket is RESOLVED */}
                {user?.role === 'REQUESTER' && ticket?.status === 'RESOLVED' && (
                  <Button 
                    variant="success" 
                    onClick={async () => {
                      try {
                        await api.patch(`/tickets/${id}`, { status: 'CLOSED' })
                        toast.success('Ticket closed successfully!', {
                          icon: '✅',
                          autoClose: 3000
                        })
                        fetchTicket()
                        window.dispatchEvent(new CustomEvent('ticket-status-changed'))
                      } catch (err: any) {
                        const errorMessage = err.response?.data?.error?.message || 'Failed to close ticket'
                        toast.error(errorMessage)
                      }
                    }}
                    className="fw-bold"
                  >
                    <i className="fas fa-check-circle me-2"></i>Confirm Resolution & Close Ticket
                  </Button>
                )}
                
                <Button variant="outline-primary" onClick={() => navigate('/tickets')}>
                  <i className="fas fa-list me-2"></i>All Tickets
                </Button>
                <Button variant="outline-success" onClick={() => navigate('/tickets/new')}>
                  <i className="fas fa-plus me-2"></i>New Ticket
                </Button>
                {(user?.role === 'ADMIN' || user?.role === 'AGENT') && (
                  <Button variant="outline-warning" onClick={() => setShowStatusModal(true)}>
                    <i className="fas fa-edit me-2"></i>Change Status
                  </Button>
                )}
                {user?.role === 'ADMIN' && (
                  <Button variant="outline-info" onClick={() => setShowAssigneeModal(true)}>
                    <i className="fas fa-user-plus me-2"></i>Assign Ticket
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Status Change Modal */}
      <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Change Ticket Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>New Status</Form.Label>
            <Form.Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
            >
              <option value="">Select status...</option>
              <option value="OPEN">Open</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="PENDING_CUSTOMER">Pending Customer</option>
              <option value="RESOLVED">Resolved</option>
              <option value="REOPENED">Reopened</option>
              {/* Only show CLOSED option for REQUESTERS when ticket is RESOLVED, or for ADMINS */}
              {((user?.role === 'REQUESTER' && ticket?.status === 'RESOLVED') || user?.role === 'ADMIN') && (
                <option value="CLOSED">Closed</option>
              )}
            </Form.Select>
          </Form.Group>
          {ticket?.status === 'RESOLVED' && user?.role === 'REQUESTER' && (
            <Alert variant="info" className="mt-3 mb-0">
              <i className="fas fa-info-circle me-2"></i>
              You can close this ticket if you're satisfied with the resolution.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleStatusChange}
            disabled={!newStatus}
          >
            Update Status
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Assignee Change Modal */}
      <Modal show={showAssigneeModal} onHide={() => setShowAssigneeModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Assign Ticket</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Ticket Summary */}
          <div className="mb-4 p-3 bg-light rounded">
            <h6 className="mb-2">
              <i className="fas fa-ticket-alt me-2 text-primary"></i>
              {ticket?.subject}
            </h6>
            <div className="d-flex gap-3 text-muted small">
              <span>
                <i className="fas fa-user me-1"></i>
                {ticket?.requester.name}
              </span>
              <span>
                <i className="fas fa-flag me-1"></i>
                {ticket?.priority}
              </span>
              <span>
                <i className="fas fa-info-circle me-1"></i>
                {ticket?.status}
              </span>
            </div>
          </div>

          {/* Attachments Section */}
          {ticket?.attachments && ticket.attachments.length > 0 && (
            <div className="mb-4">
              <h6 className="mb-3">
                <i className="fas fa-paperclip me-2 text-secondary"></i>
                Attachments ({ticket.attachments.length})
              </h6>
              <ListGroup>
                {ticket.attachments.map((attachment) => (
                  <ListGroup.Item key={attachment.id} className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <i className="fas fa-file text-muted me-2"></i>
                      <div>
                        <div className="fw-medium">{attachment.fileName}</div>
                        <small className="text-muted">{formatFileSize(attachment.size)}</small>
                      </div>
                    </div>
                    <Badge bg="secondary" pill>{attachment.contentType?.split('/')[1] || 'file'}</Badge>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          )}

          {/* Assignee Selection */}
          <Form.Group>
            <Form.Label className="fw-bold">Select Assignee</Form.Label>
            <Form.Select
              value={newAssignee}
              onChange={(e) => setNewAssignee(e.target.value)}
              size="lg"
            >
              <option value="">Unassigned</option>
              {availableUsers.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.email})
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Choose an agent to assign this ticket to
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAssigneeModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAssigneeChange}
          >
            <i className="fas fa-check me-2"></i>
            Update Assignee
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Priority Change Modal */}
      <Modal show={showPriorityModal} onHide={() => setShowPriorityModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Change Priority</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Priority</Form.Label>
            <Form.Select
              value={newPriority}
              onChange={(e) => setNewPriority(e.target.value)}
            >
              <option value="">Select priority...</option>
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
              <option value="Urgent">Urgent</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPriorityModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handlePriorityChange}
            disabled={!newPriority}
          >
            Update Priority
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Tags Change Modal */}
      <Modal show={showTagsModal} onHide={() => setShowTagsModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Manage Tags</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Tags</Form.Label>
            <div className="d-flex flex-wrap gap-1 mb-2">
              {newTags.map((tag, index) => (
                <Badge key={index} bg="primary" className="d-flex align-items-center gap-1">
                  {tag}
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    style={{ fontSize: '0.7em' }}
                    onClick={() => setNewTags(prev => prev.filter((_, i) => i !== index))}
                  />
                </Badge>
              ))}
            </div>
            <Form.Control
              type="text"
              placeholder="Add a tag"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  const input = e.target as HTMLInputElement
                  if (input.value.trim() && !newTags.includes(input.value.trim())) {
                    setNewTags(prev => [...prev, input.value.trim()])
                    input.value = ''
                  }
                }
              }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTagsModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleTagsChange}
          >
            Update Tags
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

